import React, { useState } from 'react';

const API_BASE = 'https://xijjve6puc6ht4inqmmgbss4ay0earbn.lambda-url.us-east-1.on.aws';

interface UnsubscribeResult {
    success: boolean;
    message: string;
    contact?: {
        email: string;
        first_name?: string;
        last_name?: string;
        status: string;
    };
}

const UnsubscribeManager: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<UnsubscribeResult | null>(null);

    const handleUnsubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!email.trim()) {
            setResult({
                success: false,
                message: 'Please enter an email address'
            });
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setResult({
                success: false,
                message: 'Please enter a valid email address'
            });
            return;
        }

        setIsLoading(true);
        setResult(null);

        try {
            const token = localStorage.getItem('idToken');
            const response = await fetch(`${API_BASE}/contacts/unsubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ email: email.trim().toLowerCase() })
            });

            const data = await response.json();
            setResult(data);

            if (data.success) {
                // Clear the email field on success
                setEmail('');
            }
        } catch (error) {
            console.error('Error unsubscribing:', error);
            setResult({
                success: false,
                message: 'Network error. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="unsubscribe-manager">
            <div className="unsubscribe-header">
                <h1>🚫 Unsubscribe Management</h1>
                <p className="header-subtitle">Remove contacts from email campaigns</p>
            </div>

            <div className="unsubscribe-content">
                <div className="unsubscribe-card">
                    <div className="card-header">
                        <h2>Unsubscribe Email Address</h2>
                        <p>Enter an email address to unsubscribe from all future campaigns</p>
                    </div>

                    <form onSubmit={handleUnsubscribe} className="unsubscribe-form">
                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="user@example.com"
                                className="form-input"
                                disabled={isLoading}
                                autoComplete="off"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn-unsubscribe"
                            disabled={isLoading || !email.trim()}
                        >
                            {isLoading ? '⏳ Processing...' : '🚫 Unsubscribe'}
                        </button>
                    </form>

                    {result && (
                        <div className={`result-message ${result.success ? 'success' : 'error'}`}>
                            <div className="result-icon">
                                {result.success ? '✅' : '❌'}
                            </div>
                            <div className="result-content">
                                <strong>{result.success ? 'Success!' : 'Error'}</strong>
                                <p>{result.message}</p>
                                {result.contact && (
                                    <div className="contact-info">
                                        <p>
                                            {result.contact.first_name && result.contact.last_name
                                                ? `${result.contact.first_name} ${result.contact.last_name}`
                                                : result.contact.email}
                                        </p>
                                        <span className="status-badge">{result.contact.status}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="info-panel">
                    <h3>ℹ️ Important Information</h3>
                    <ul>
                        <li>
                            <strong>Permanent Action:</strong> Unsubscribing will set the contact status to "unsubscribed"
                        </li>
                        <li>
                            <strong>Campaign Exclusion:</strong> Unsubscribed contacts will not receive any future campaigns
                        </li>
                        <li>
                            <strong>Status Check:</strong> You can verify unsubscribe status in the Email Contacts page
                        </li>
                        <li>
                            <strong>Re-subscription:</strong> To re-subscribe a contact, update their status in Email Contacts
                        </li>
                    </ul>

                    <div className="warning-box">
                        <strong>⚠️ Note:</strong> This action is immediate and will affect all active campaigns.
                        Make sure you have the correct email address before proceeding.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnsubscribeManager;

